/**
 * applyDetailSurface — patch a NodeMaterial with a procedural micro-normal +
 * roughness breakup (POM-pattern: caches prior nodes, returns an update/dispose
 * handle). No texture assets, no UV/tangent dependency (derivative-based TBN).
 */
import type * as THREE from 'three/webgpu';
import { float, vec3, materialNormal, materialRoughness, tangentView, bitangentView } from 'three/tsl';
import { Logger } from '../../../core/Logger.js';
import { createDetailConfig, type DetailSurfaceOptions } from './DetailConfig.js';
import { createDetailNodes } from './DetailNode.js';
import { resolveSurfaceGradient, surfgradFromTBN, tspaceNormalToDerivative } from '../SurfaceGradient.js';

export interface DetailHandle {
  /** Push config changes to the GPU. */
  update(): void;
  /** Restore the material's pre-detail nodes. */
  dispose(): void;
}

export function applyDetailSurface(
  material: THREE.MeshStandardNodeMaterial | THREE.NodeMaterial,
  opts: DetailSurfaceOptions = {},
): DetailHandle {
  const config = createDetailConfig(opts);
  const { detailNormal, roughnessFactor, keep, uniforms } = createDetailNodes(config);

  const matAny = material as unknown as { normalNode?: unknown; roughnessNode?: unknown; needsUpdate?: boolean };
  const prior = { normalNode: matAny.normalNode, roughnessNode: matAny.roughnessNode };

  // Detail normal → a VIEW-SPACE shading normal. three r185 uses `normalNode`
  // VERBATIM as normalView (NodeMaterial.setupNormal); assigning the raw
  // [0,1]-encoded tangent vector made every fragment's normal a near-constant
  // camera-facing (0.5, 0.5, 1) — a side-lit sphere shaded its dark hemisphere
  // as bright as its lit one, and every structure on med+ lit alike
  // (Atlas verify:detail-normal:probe, 2026-09-22). Decode the tangent-space
  // detail and BEND the material's own normal along the tangent frame: the
  // prior normalNode if one was set, else `materialNormal` (the authored normal
  // map through its TBN, or the geometry normal) — so an authored normal map is
  // kept and the detail rides on top of it. Inside the NORMAL sub-build the
  // frame comes from the geometry (or screen derivatives without tangents).
  //
  // THE NORMAL CONTRACT (material models, 2026-09-30): the bend IS Mikkelsen's TBN surface gradient of the decoded
  // tangent-space detail (d.z = 1 ⇒ derivative −d.xy), resolved once against the base — normalize(n + d.x·T + d.y·B),
  // the same arithmetic as before (SurfaceGradient.ts; tests/unit/material-models.test.ts pins the equality).
  const d = detailNormal.mul(2).sub(1);
  const baseNormal = prior.normalNode ? vec3(prior.normalNode as never) : materialNormal;
  matAny.normalNode = resolveSurfaceGradient(baseNormal, surfgradFromTBN(tspaceNormalToDerivative(d), tangentView, bitangentView));

  // Roughness breakup around the material's own roughness — the prior
  // roughnessNode (e.g. a wet / snow accumulation layer) if set, else the
  // authored scalar × its roughness map (`materialRoughness`); the scalar alone
  // used to discard both. Plus — when filtered — the slope energy of the detail
  // the footprint no longer resolves (≈ s²/2 for a normal perturbation of
  // amplitude s), so a distant surface roughens instead of turning falsely
  // glossy as its bumps average out.
  const baseRough = prior.roughnessNode ? float(prior.roughnessNode as never) : materialRoughness;
  const unresolved = float(1).sub(keep);
  matAny.roughnessNode = baseRough
    .add(roughnessFactor.mul(0.5).mul(config.roughnessBreakup))
    .add(unresolved.mul(config.roughnessTransfer * config.normalStrength * config.normalStrength))
    .clamp(0.04, 1.0);

  matAny.needsUpdate = true;
  Logger.debug('DetailSurface', `applied (tiling=${config.tiling}, normalStrength=${config.normalStrength}, roughnessBreakup=${config.roughnessBreakup})`);

  return {
    update(): void {
      (uniforms.tiling as unknown as { value: number }).value = config.tiling;
      (uniforms.normalStrength as unknown as { value: number }).value = config.normalStrength;
    },
    dispose(): void {
      matAny.normalNode = prior.normalNode;
      matAny.roughnessNode = prior.roughnessNode;
      matAny.needsUpdate = true;
    },
  };
}
